import { lazy, Suspense } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth/AuthProvider";
import { LoginPage } from "./auth/LoginPage";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { DataStatus } from "./components/DataStatus";
import { useTerritory } from "./context/TerritoryContext";

const AdminPage = lazy(() => import("./admin").then((module) => ({ default: module.AdminPage })));
const DeforestationPage = lazy(() => import("./analysis/DeforestationPage").then((module) => ({ default: module.DeforestationPage })));
const GeoViewerPage = lazy(() => import("./map/GeoViewerPage").then((module) => ({ default: module.GeoViewerPage })));
const TribesPage = lazy(() => import("./tribes/TribesPage").then((module) => ({ default: module.TribesPage })));
const StoriesPage = lazy(() => import("./stories/StoriesPage").then((module) => ({ default: module.StoriesPage })));

function ModuleFallback() { return <section className="workspace" aria-live="polite"><p>Cargando módulo…</p></section>; }

const modules = [
  { path: "/visor", label: "Geovisor", description: "Territorios, población y capas ICF" },
  { path: "/deforestacion", label: "Deforestación", description: "Cobertura y pérdida forestal" },
  { path: "/tribus", label: "Fichas", description: "Información organizada por tribu" },
  { path: "/historias", label: "Geostories", description: "Narrativas territoriales aprobadas" },
];

const pendingModules = modules.filter((module) => !["/visor", "/deforestacion", "/tribus", "/historias"].includes(module.path));

function Placeholder({ title }: { title: string }) {
  return (
    <section className="workspace">
      <div>
        <p className="eyebrow">Módulo preparado</p>
        <h2>{title}</h2>
        <p>El contenido del prototipo existente se migrará aquí durante las siguientes fases.</p>
      </div>
    </section>
  );
}

function Home() {
  return (
    <section className="hero">
      <div className="hero-copy">
        <p className="eyebrow">Portal territorial (privado)</p>
        <h2>Protección y defensa del territorio.</h2>
        <p className="hero-subtitle">Plataforma de gestión de información para la toma de decisiones.</p>
        <DataStatus />
      </div>
      <div className="module-grid">
        {modules.map((module) => (
          <NavLink className="module-card" key={module.path} to={module.path}>
            <strong>{module.label}</strong>
            <span>{module.description}</span>
          </NavLink>
        ))}
      </div>
    </section>
  );
}

function ConfigurationPage() {
  const { configured } = useAuth();
  if (configured) return <Navigate replace to="/" />;

  return (
    <section className="workspace">
      <div>
        <p className="eyebrow">Configuración requerida</p>
        <h2>Conecta Supabase</h2>
        <p>
          Copia <code>.env.example</code> como <code>.env</code> y completa las dos variables
          públicas de Supabase.
        </p>
      </div>
    </section>
  );
}

export function App() {
  const { profile, signOut } = useAuth();
  const { territory, selectTerritory } = useTerritory();

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Saltar al contenido principal</a>
      <header className="topbar">
        <NavLink className="brand" to="/">
          <img src="https://geoeit.org/Logo_Geoportal_Light_v2.png" alt="Logo Geoportal EIT" className="brand-logo" />
          <span>
            <strong>Territorios Tolupanes</strong>
            <small>Consulta y validación territorial</small>
          </span>
        </NavLink>
        {profile && (
          <nav aria-label="Navegación principal">
            {modules.map((module) => (
              <NavLink key={module.path} to={module.path}>{module.label}</NavLink>
            ))}
            {profile.role !== "viewer" && <NavLink to="/admin">Administración</NavLink>}
            <button className="nav-button" onClick={() => void signOut()} type="button">Salir</button>
          </nav>
        )}
      </header>
      {profile && territory && <aside className="territory-context" aria-label="Territorio activo">
        <span>Territorio activo: <strong>{territory.name}</strong></span>
        <nav aria-label="Acciones del territorio activo"><NavLink to={`/tribus/${territory.id}`}>Ficha</NavLink><NavLink to={`/visor?tribu=${territory.id}`}>Mapa</NavLink><NavLink to={`/deforestacion?territorio=${territory.id}`}>Análisis</NavLink><button onClick={() => selectTerritory(null)} type="button">Limpiar</button></nav>
      </aside>}
      <main id="main-content" tabIndex={-1}>
        <Suspense fallback={<ModuleFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/configuration" element={<ConfigurationPage />} />
          <Route path="/sin-acceso" element={<Placeholder title="Acceso no autorizado" />} />
          <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/visor/*" element={<ProtectedRoute><GeoViewerPage /></ProtectedRoute>} />
          <Route path="/deforestacion/*" element={<ProtectedRoute><DeforestationPage /></ProtectedRoute>} />
          <Route path="/tribus" element={<ProtectedRoute><TribesPage /></ProtectedRoute>} />
          <Route path="/tribus/:tribeId" element={<ProtectedRoute><TribesPage /></ProtectedRoute>} />
          <Route path="/historias" element={<ProtectedRoute><StoriesPage /></ProtectedRoute>} />
          <Route path="/historias/:storySlug" element={<ProtectedRoute><StoriesPage /></ProtectedRoute>} />
          {pendingModules.map((module) => (
            <Route
              key={module.path}
              path={`${module.path}/*`}
              element={<ProtectedRoute><Placeholder title={module.label} /></ProtectedRoute>}
            />
          ))}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute minimumRole="specialist">
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
        </Suspense>
      </main>
      <footer>
        <span>Portal privado</span>
        <span>Los permisos efectivos se aplican también mediante Row Level Security.</span>
      </footer>
    </div>
  );
}